'use client'

import { useConsent } from '@/contexts/ConsentContext'
import getAPI from '@/lib/utils/api-client'
import { cn } from '@/lib/utils/utils'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import validator from 'validator'
import { getI18n, I18nDictionary, LangCode } from '../i18n/lang-context'
import Reveal from '../reveal'
import { Button } from '../ui/button'
import { FlexCol } from '../ui/flex'
import { Input } from '../ui/input'
import { useCookiesConsentDialog } from './cookies-dialog'
import { Section } from './section'
import { SectionTitle } from './section-title'

const I18N: I18nDictionary = {
  title: {
    en: 'Our monitoring of crypto investment for the real economy, in your email inbox.',
    fr: 'Notre veille sur l’utilisation des crypto-actifs dans l’économie réelle, directement dans votre boîte e-mail.'
  },
  desc: {
    en: 'Subscribe to our newsletter to receive our latest articles and news!',
    fr: 'Inscrivez-vous à notre newsletter pour recevoir nos derniers articles et actualités !'
  },
  firstNamePlaceholder: { en: 'First Name', fr: 'Prénom' },
  lastNamePlaceholder: { en: 'Last Name', fr: 'Nom' },
  emailPlaceholder: { en: 'Email Address', fr: 'Adresse Email' },
  subscribe: { en: 'Subscribe', fr: 'S’inscrire' },
  firstNameInvalid: {
    en: 'Invalid first name!',
    fr: 'Prénom invalide !'
  },
  lastNameInvalid: {
    en: 'Invalid lastName!',
    fr: 'Nom invalide !'
  },
  emailInvalid: {
    en: 'Invalid email address!',
    fr: 'Adresse e-mail invalide !'
  },
  alreadyExists: {
    en: 'Oops! It looks like this email address is already subscribed...',
    fr: 'Oups ! Il semble que cette adresse e-mail soit déjà inscrite...'
  },
  unknownError: {
    en: 'Oops ! An unknown error occured...',
    fr: 'Oups ! Une erreur inconnue s\'est produite...'
  },
  keepInTouch: {
    en: 'Thank you! You\'ve successfully subscribed. We\'ll be in touch soon!',
    fr: 'Merci ! Votre e-mail a été enregistré avec succès. À bientôt !'
  },
  subscribing: {
    en: 'Subscribing...',
    fr: 'Inscription...'
  },
  subscribeNew: {
    en: 'Subscribe another email',
    fr: 'Abonnez une autre adresse e-mail'
  }
}

export function NewsletterSubscription({ background2, langCode }: { background2?: boolean, langCode: LangCode }) {

  const i18n = getI18n(I18N, langCode)
  const api = getAPI()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [firstNameValid, setFirstNameValid] = useState(false)
  const [lastNameValid, setLastNameValid] = useState(false)
  const [emailValid, setEmailValid] = useState(false)
  const [subscribing, setSubscribing] = useState(false)
  const [subscribed, setSubscribed] = useState(false)
  const { consent } = useConsent()

  const validateFirstName = (e: any) => {
    const name = e.target.value.toLowerCase()
    if (name !== '') {
      setFirstNameValid(true)
    } else {
      setFirstNameValid(false)
    }
    setFirstName(name)
  }
  const validateLastName = (e: any) => {
    const name = e.target.value.toLowerCase()
    if (name !== '') {
      setLastNameValid(true)
    } else {
      setLastNameValid(false)
    }
    setLastName(name)
  }
  const validateEmail = (e: any) => {
    const email = e.target.value.toLowerCase()
    if (email !== '') {
      if (validator.isEmail(email)) {
        setEmailValid(true)
      } else {
        setEmailValid(false)
      }
    } else {
      setEmailValid(false)
    }
    setEmail(email)
  }

  const _doSubscribe = async () => {
    if (!firstNameValid) return
    if (!lastNameValid) return
    if (!emailValid) return
    try {
      setSubscribing(true)
      const subscription = await api.post('/email/newsletter/subscribe', { firstName, lastName, email, langCode })
      console.log('POST /email/newsletter/subscribe RESPONSE', subscription)
      setSubscribed(true)
      setEmailValid(false)
      toast.success(i18n.keepInTouch, { autoClose: 5000 })
    } catch (e: any) {
      console.error('POST /email/newsletter/subscribe ERROR: ' + e.errorCode, e)
      toast.error(i18n.unknownError, { autoClose: 5000 })
    }
    setSubscribing(false)
  }

  const { openCookiesConsentDialog } = useCookiesConsentDialog(langCode, _doSubscribe)
  
  const subscribe = async () => {
    if (consent.contact) {
      _doSubscribe()
    } else {
      openCookiesConsentDialog('simple', langCode)
    }
  }

  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  if (!mounted) return null

  return (
    <Section containerClassName={cn(background2 ? 'bg-background2' : 'bg-background')}>

      <FlexCol id='NEWSLETTER_PANEL' className={'relative w-full max-w-5xl gap-8 overflow-hidden rounded-lg bg-primary p-4 text-white sm:p-16'}>

        <div id='TRIANGLE_1' className='absolute -left-40 top-[80%] z-[1] h-full w-[200%] rotate-12 bg-white opacity-20 md:-left-80 xl:top-[90%]' />
        <div id='TRIANGLE_2' className='absolute -right-40 top-[80%] z-[2] h-full w-[200%] -rotate-12 bg-black opacity-10 md:-right-80 xl:top-[90%]' />

        <SectionTitle title={i18n.title} desc={i18n.desc} langCode={langCode} contrast={true} titleMaxWidth='max-w-4xl' descMaxWidth='max-w-xl' className='relative z-10' />

        {!subscribed &&
        <Reveal id='SUBSCRIBE' duration={250} delay={250} scale={0} className='relative z-10 w-full'>
          <div className='flex w-full flex-col md:flex-row flex-nowrap items-center justify-center gap-8 md:gap-6'>
            <div className='relative w-full max-w-sm md:max-w-72'>
              <Input id='firstName' placeholder={i18n.firstNamePlaceholder} className={cn('max-w-sm', firstNameValid || firstName==='' ? 'border-none' : 'border-red-600', 'bg-white p-6 text-black placeholder:text-black placeholder:opacity-50')}
                disabled={subscribing} onChange={validateFirstName} onKeyDown={(e) => e.key === 'Enter' && subscribe()} />
              <div className='absolute bottom-[-24px] left-2 whitespace-nowrap text-sm text-red-500'>{firstNameValid || firstName==='' ? '' : i18n.firstNameInvalid}</div>
            </div>
            <div className='relative w-full max-w-sm md:max-w-72'>
              <Input id='lastName' placeholder={i18n.lastNamePlaceholder} className={cn('max-w-sm', lastNameValid || lastName==='' ? 'border-none' : 'border-red-600', 'bg-white p-6 text-black placeholder:text-black placeholder:opacity-50')}
                disabled={subscribing} onChange={validateLastName} onKeyDown={(e) => e.key === 'Enter' && subscribe()} />
              <div className='absolute bottom-[-24px] left-2 whitespace-nowrap text-sm text-red-500'>{lastNameValid || lastName==='' ? '' : i18n.lastNameInvalid}</div>
            </div>
          </div>
          <div className='flex w-full flex-col md:flex-row flex-nowrap items-center justify-center gap-8 md:gap-4 mt-8'>
            <div className='relative w-full max-w-sm'>
              <Input id='email' placeholder={i18n.emailPlaceholder} className={cn('max-w-sm', emailValid || email==='' ? 'border-none' : 'border-red-600', 'bg-white p-6 text-black placeholder:text-black placeholder:opacity-50')}
                disabled={subscribing} onChange={validateEmail} onKeyDown={(e) => e.key === 'Enter' && subscribe()} />
              <div className='absolute bottom-[-24px] left-2 whitespace-nowrap text-sm text-red-500'>{emailValid || email==='' ? '' : i18n.emailInvalid}</div>
            </div>
            <Button id='subscribe' className={cn('mb-6 w-[200px] bg-white p-6 px-12 font-semibold ',
              !firstNameValid || !lastNameValid || !emailValid || subscribing ? 'text-gray-500 italic' : 'text-black', 'md:m-0')}
              disabled={!firstNameValid || !lastNameValid || !emailValid || subscribing} onClick={subscribe}>{subscribing ? i18n.subscribing : i18n.subscribe}</Button>
          </div>
        </Reveal>}

        {subscribed && <Reveal id='SUBSCRIBE' duration={250} delay={250} scale={0} className='relative z-10 flex w-full flex-col flex-nowrap items-center justify-center gap-4'>
          <div className='text-xl font-semibold'>{firstName + ' ' + lastName}</div>
          <div className='text-center text-lg'>{i18n.keepInTouch}</div>
          <Button className='mb-6 bg-white p-6 px-12 font-semibold text-black md:m-0' onClick={() => { setSubscribed(false); setFirstName(''); setLastName(''); setEmail('') }}>{i18n.subscribeNew}</Button>
        </Reveal>}

      </FlexCol>

    </Section>
  )
}
